import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { ArrowLeft, CalendarDays, Check, Clock, MapPin, Plus, Timer } from "lucide-react";
import LoadingDots from "../../atoms/Utlis/LoadinDots";
import HourPicker from "../../atoms/Dashboard/Events/HourPicker";
import LocationPickerModal from "../../atoms/Dashboard/Events/SetLocationModal";
import { EnteredSubEventType } from "../../../Data/event.dto";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

interface SubEventScreenProps {
  onClose: () => void;
  addSubEvent: (event: EnteredSubEventType) => void;
  currentSubEvent?: EnteredSubEventType | null;
  locationList: string[];
  setLocationList: (list: string[]) => void;
  currentLength: number;
}

const defaultSelectedHour = undefined;

const fieldClass =
  "w-full mt-2 px-4 py-3.5 rounded-2xl bg-white border border-black/10 text-[#171717] font-medium placeholder:text-black/35 placeholder:font-normal focus:outline-none focus:border-[#CE56B0]/50 focus:ring-4 focus:ring-[#CE56B0]/10 transition";

const SubEventScreen: React.FC<SubEventScreenProps> = ({
  onClose,
  addSubEvent,
  currentSubEvent,
  locationList,
  setLocationList,
  currentLength,
}) => {
  const [eventName, setEventName] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [isLocationListOpen, setIsLocationListOpen] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [selectedHours, setSelectedHours] = useState<
    { value: number; label: string } | undefined
  >(defaultSelectedHour);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  const [id, setId] = useState(currentLength + 1);
  const [locationPickerModal, setLocationPickerModal] = useState(false);

  useEffect(() => {
    const updateViewport = () => setIsMobileView(window.innerWidth < 768);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (currentSubEvent) {
      setEventName(currentSubEvent.sub_event_name);
      setEventLocation(currentSubEvent.sub_event_location);
      setStartDate(currentSubEvent.sub_event_start_date_time);
      setStartTime(currentSubEvent.sub_event_start_date_time);
      setSelectedHours({
        label: `${currentSubEvent.duration}`,
        value: currentSubEvent.duration,
      });
      setId(currentSubEvent.id);
    } else {
      resetForm();
      setId(currentLength + 1);
    }
    setShowError(false);
  }, [currentSubEvent, currentLength]);

  const validateEventName = (name: string) => name.length > 0 && name.length < 40;
  const validateLocation = (loc: string) => loc.length > 0 && loc.length < 300;

  const formatEnterDate = (dateString: string) => {
    const d = new Date(dateString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hr = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${day}T${hr}:${min}`;
  };

  const parseDate = (formattedDate: string) => {
    const [datePart, timePart] = formattedDate.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  };

  const giveFormattedDateTime = (value: string) => {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return {
      formattedDate: `${year}-${month}-${day}`,
      formattedTime: `${hours}:${minutes}:${seconds}`,
    };
  };

  const setEventDate = (dateTime: string) => {
    const formatted = giveFormattedDateTime(dateTime);
    return {
      start_date: formatted.formattedDate,
      start_time: formatted.formattedTime,
    };
  };

  const resetForm = () => {
    setEventName("");
    setEventLocation("");
    setStartDate("");
    setStartTime("");
    setSelectedHours(undefined);
  };

  const submitHandler = () => {
    const isPastDate = startDate
      ? dayjs(startDate).isBefore(dayjs().startOf("day"))
      : false;
    if (
      !validateEventName(eventName) ||
      !validateLocation(eventLocation) ||
      !startDate ||
      !startTime ||
      !selectedHours ||
      isPastDate
    ) {
      setShowError(true);
      return;
    }

    setLoading(true);

    const { start_date } = setEventDate(parseDate(startDate).toString());
    const { start_time } = setEventDate(parseDate(startTime).toString());

    addSubEvent({
      sub_event_name: eventName,
      sub_event_location: eventLocation,
      starting_date: start_date,
      starting_time: start_time,
      duration: selectedHours.value,
      sub_event_start_date_time: startDate,
      sub_event_coordinates: "test coordinates",
      id,
    });

    setLoading(false);
  };

  const setLocation = (loc: string) => {
    setLocationList([...locationList, loc]);
    setEventLocation(loc);
    setLocationPickerModal(false);
    setIsLocationListOpen(false);
  };

  return (
    <>
      {locationPickerModal && (
        <LocationPickerModal
          isOpen={locationPickerModal}
          onClose={() => setLocationPickerModal(false)}
          onSelect={setLocation}
        />
      )}

      <div className="flex flex-col gap-6">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex w-fit items-center gap-2 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm font-semibold text-black/60 transition hover:border-[#CE56B0]/30 hover:text-[#C94DAA]"
        >
          <ArrowLeft size={16} />
          Back to event details
        </button>

        <div className="rounded-2xl border border-[#CE56B0]/15 bg-gradient-to-br from-[#FFC951]/10 to-[#CE56B0]/5 px-4 py-3">
          <p className="text-sm font-semibold text-[#171717]">
            {currentSubEvent ? "Editing day schedule" : "New day schedule"}
          </p>
          <p className="mt-0.5 text-xs font-medium leading-relaxed text-black/50">
            Fill in the name, timing, and venue for this shoot day. Required fields are marked *.
          </p>
        </div>

        {/* Day name */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg background-linear text-[11px] font-bold text-white">
              1
            </span>
            <div>
              <h3 className="font-[Urbanist,sans-serif] text-base font-bold text-[#171717]">
                Day name <span className="text-red-500">*</span>
              </h3>
              <p className="text-xs font-medium text-black/45">
                Examples: Engagement, Haldi, Wedding day, Reception
              </p>
            </div>
          </div>
          <input
            type="text"
            placeholder="e.g. Wedding day"
            className={fieldClass}
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
          />
          {showError && !validateEventName(eventName) && (
            <p className="text-sm text-red-500">Please enter a day name.</p>
          )}
        </section>

        {/* When */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg background-linear text-[11px] font-bold text-white">
              2
            </span>
            <div>
              <h3 className="font-[Urbanist,sans-serif] text-base font-bold text-[#171717]">
                When is it? <span className="text-red-500">*</span>
              </h3>
              <p className="text-xs font-medium text-black/45">
                Pick the date, start time, and how long the shoot lasts.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 rounded-2xl border border-black/6 bg-[#FAFAFA] p-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-black/45">
                <CalendarDays size={12} />
                Date
              </label>
              <div className="w-full event_date_sub">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {isMobileView ? (
                    <MobileDatePicker
                      open={isDatePickerOpen}
                      onClose={() => setIsDatePickerOpen(false)}
                      onAccept={() => setIsDatePickerOpen(false)}
                      value={startDate ? dayjs(startDate) : null}
                      format="DD/MM/YYYY"
                      minDate={dayjs().startOf("day")}
                      onChange={(e) =>
                        e && setStartDate(formatEnterDate(e.toDate().toString()))
                      }
                      slotProps={{
                        textField: {
                          onClick: () => setIsDatePickerOpen(true),
                          inputProps: { readOnly: true },
                        },
                      } as any}
                    />
                  ) : (
                    <DatePicker
                      value={startDate ? dayjs(startDate) : null}
                      format="DD/MM/YYYY"
                      desktopModeMediaQuery="@media (min-width: 768px)"
                      minDate={dayjs().startOf("day")}
                      slotProps={{
                        textField: {
                          onClick: (e: any) => {
                            e.currentTarget.querySelector("button")?.click();
                          },
                        },
                      } as any}
                      onChange={(e) =>
                        e && setStartDate(formatEnterDate(e.toDate().toString()))
                      }
                    />
                  )}
                </LocalizationProvider>
                {showError &&
                  (!startDate ||
                    dayjs(startDate).isBefore(dayjs().startOf("day"))) && (
                    <p className="mt-1 text-sm text-red-500">
                      {!startDate
                        ? "Please choose a date."
                        : "Past dates cannot be selected."}
                    </p>
                  )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-black/45">
                <Clock size={12} />
                Start time
              </label>
              <div className="event_time_sub">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {isMobileView ? (
                    <MobileTimePicker
                      open={isTimePickerOpen}
                      onClose={() => setIsTimePickerOpen(false)}
                      onAccept={() => setIsTimePickerOpen(false)}
                      value={startTime ? dayjs(startTime) : null}
                      onChange={(e) =>
                        e && setStartTime(formatEnterDate(e.toDate().toString()))
                      }
                      slotProps={
                        {
                          textField: {
                            onClick: () => setIsTimePickerOpen(true),
                            inputProps: { readOnly: true },
                          },
                        } as any
                      }
                    />
                  ) : (
                    <TimePicker
                      value={startTime ? dayjs(startTime) : null}
                      desktopModeMediaQuery="@media (min-width: 768px)"
                      onChange={(e) =>
                        e && setStartTime(formatEnterDate(e.toDate().toString()))
                      }
                      slotProps={
                        {
                          textField: {
                            onClick: (e: any) => {
                              e.currentTarget.querySelector("button")?.click();
                            },
                          },
                        } as any
                      }
                    />
                  )}
                </LocalizationProvider>
                {showError && !startTime && (
                  <p className="mt-1 text-sm text-red-500">Please choose a start time.</p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-black/45">
                <Timer size={12} />
                Duration (hours)
              </label>
              <div className="event_hours_sub max-w-xs">
                <HourPicker
                  value={selectedHours}
                  onChange={(h) => setSelectedHours({ label: `${h}`, value: h })}
                />
                {!selectedHours && showError && (
                  <p className="mt-1 text-sm text-red-500">
                    Please select how many hours this day lasts.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Location */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg background-linear text-[11px] font-bold text-white">
              3
            </span>
            <div>
              <h3 className="font-[Urbanist,sans-serif] text-base font-bold text-[#171717]">
                Where is it? <span className="text-red-500">*</span>
              </h3>
              <p className="text-xs font-medium text-black/45">
                Choose a saved venue or add a new location on the map.
              </p>
            </div>
          </div>

          <button
            type="button"
            className={`${fieldClass} mt-0 flex items-center justify-between gap-3 text-left`}
            onClick={() => setIsLocationListOpen((v) => !v)}
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl background-linear text-white">
                <MapPin size={14} />
              </span>
              <span
                className={`truncate ${
                  eventLocation ? "font-bold text-[#171717]" : "font-medium text-black/35"
                }`}
              >
                {eventLocation || "Tap to choose location"}
              </span>
            </span>
          </button>

          {isLocationListOpen && (
            <div className="rounded-2xl border border-black/8 bg-white p-3 shadow-lg shadow-black/5">
              <div className="mb-2 flex items-center justify-between gap-3 px-1">
                <h4 className="text-sm font-bold text-[#171717]">Saved locations</h4>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg background-linear px-3 py-1.5 text-xs font-bold text-white"
                  onClick={() => setLocationPickerModal(true)}
                >
                  <Plus size={13} />
                  Add new
                </button>
              </div>

              {locationList.length > 0 ? (
                <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                  {locationList.map((loc) => {
                    const active =
                      loc.toLowerCase() === eventLocation.toLowerCase();
                    return (
                      <button
                        type="button"
                        key={loc}
                        className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                          active
                            ? "bg-[#CE56B0]/08 font-bold text-[#171717]"
                            : "font-medium text-black/70 hover:bg-black/[0.03]"
                        }`}
                        onClick={() => {
                          setEventLocation(loc);
                          setIsLocationListOpen(false);
                        }}
                      >
                        <span className="truncate">{loc}</span>
                        {active ? (
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md background-linear text-white">
                            <Check size={12} strokeWidth={3} />
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
                  <MapPin size={18} className="text-[#C94DAA]" />
                  <p className="text-sm font-medium text-black/45">
                    No locations yet. Tap Add new to create one.
                  </p>
                </div>
              )}
            </div>
          )}

          {showError && !validateLocation(eventLocation) && (
            <p className="text-sm text-red-500">Please select or add a location.</p>
          )}
        </section>

        <div className="flex justify-center pt-2">
          {loading ? (
            <LoadingDots />
          ) : (
            <button
              type="button"
              className="w-full max-w-xs rounded-2xl background-linear px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-[#CE56B0]/25 transition hover:-translate-y-0.5 sm:w-auto"
              onClick={submitHandler}
            >
              {currentSubEvent ? "Save day schedule" : "Add day & continue"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default SubEventScreen;
